<template>
	<v-card tile elevation="0" style="height: 100%; overflow-y: hidden">
		<v-card-title class="text-left card-header fixed-card-title2 py-0" style="height: 6%">
			<v-row>
				<v-col cols="10">
					<v-toolbar-title>{{ $t('add-medication') }}</v-toolbar-title>
				</v-col>
				<v-col cols="2" class="text-right">
					<v-icon color="white" @click="closeDrawer">mdi mdi-close</v-icon>
				</v-col>
			</v-row>
		</v-card-title>
		<v-container style="height: 10.4%">
			<v-col cols="12" class="pa-0">
				<v-text-field
					class="pa-0"
					v-model="searchText"
					:label="$t('search')"
					:value="searchText"
					prepend-inner-icon="mdi-magnify"
					outlined
					dense
					clearable
					hide-details
					@keypress.enter="searchForPrescriptions" />
			</v-col>
			<v-col cols="12" class="pa-0 pt-3">
				<v-btn block color="dark_purple" style="color: white" @click="searchForPrescriptions">{{ $t('search') }}</v-btn>
			</v-col>
		</v-container>
		<v-radio-group v-model="selectedPrescription" dense hide-details class="my-0 pa-0" style="height: 41.31%">
			<v-data-table
				:loading="loading"
				:headers="headers"
				:no-data-text="$t('no-data-available')"
				:loading-text="$t('loading')"
				:items="searchedPrescriptions"
				:items-per-page="-1"
				sort-by="brandName"
				fixed-header
				height="35.66vh"
				style="width: 100%; overflow-y: auto"
				dense
				class="medical-staff-data-table">
				<template v-slot:item="{ item, index }">
					<v-tooltip left>
						<template v-slot:activator="{ on, attrs }">
							<tr :class="getRowsForDataTable(index)" v-bind="attrs" v-on="on" @click="selectedPrescription = item">
								<td class="font-size13 text-left pl-2 pr-0">
									{{ item.brandName }}
								</td>
								<td class="font-size13 text-left pl-2 pr-0">
									{{ item.dosageInstructions }}
								</td>
								<td class="font-size13 text-left pl-2 pr-0">
									{{ item.route }}
								</td>
								<td class="font-size13 text-left pl-2 pr-0">
									<v-radio :value="item" />
								</td>
							</tr>
						</template>
						<span>{{ item.manufacturerName }}</span>
					</v-tooltip>
				</template>
			</v-data-table>
		</v-radio-group>
		<v-container style="height: 36%">
			<v-row dense>
				<v-col cols="12" class="mx-0">
					<v-text-field outlined dense v-model="dosage" :label="$t('dosage')" hide-details />
				</v-col>
				<v-col cols="6" class="mx-0">
					<v-text-field outlined dense v-model="frequency" :label="$t('frequency')" hide-details />
				</v-col>
				<v-col cols="6" class="mx-0">
					<v-text-field
						outlined
						dense
						v-model="period"
						:label="$t('period')"
						hide-details
						:suffix="$t('days')"
						@keypress="filterNumbersOnly" />
				</v-col>
				<v-col cols="12" class="mx-0">
					<v-select
						outlined
						dense
						v-model="prescribingDoctor"
						:label="$t('prescribing-doctor')"
						:items="doctors"
						return-object
						item-text="fullName"
						:menu-props="{ maxHeight: 200, offsetY: true }"
						hide-details />
				</v-col>
				<v-col cols="12" class="mx-0">
					<v-textarea
						outlined
						dense
						no-resize
						rows="8"
						v-model="additionalInfo"
						:label="$t('additional-information')"
						hide-details />
				</v-col>
			</v-row>
		</v-container>
		<v-container style="height: 6.3%">
			<v-row dense>
				<v-col cols="6" order="1">
					<v-btn @click="closeDrawer()" color="secondary" style="color: black" block>{{ $t('cancel') }}</v-btn>
				</v-col>
				<v-tooltip left v-if="!addButtonEnabled">
					<template v-slot:activator="{ on, attrs }">
						<v-col cols="6" v-bind="attrs" v-on="on" order="2">
							<v-btn disabled block>{{ $t('prescribe') }}</v-btn>
						</v-col>
					</template>
					<span>{{ $t('fill-all-fields-medication-warning') }}</span>
				</v-tooltip>
				<v-col cols="6" v-else order="2">
					<v-btn @click="addPrescription()" color="dark_purple" style="color: white" block>{{ $t('prescribe') }}</v-btn>
				</v-col>
			</v-row>
		</v-container>
	</v-card>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { getRowsForDataTable } from '@/utils/utilities';

export default {
	data () {
		return {
			loading: false,
			searchText: '',
			dosage: '',
			frequency: '',
			period: '',
			additionalInfo: '',
			doctors: null,
			prescribingDoctor: null,
			selectedPrescription: null,
		};
	},

	computed: {
		...mapState({
			userData: (state) => state.authentication.userData,
			detailedUser: (state) => state.users.detailedUser,
			prescriptions: (state) => state.prescriptions.prescriptions,
			searchedPrescriptions: (state) => state.prescriptions.searchedPrescriptions,
		}),
		...mapGetters({
			pageHeight: 'pageHeight',
		}),
		textareaRows () {
			return this.$vuetify.breakpoint.lgAndUp ? 30 : 19;
		},
		headers () {
			return [
				{
					text: this.$t('prescription-brand-name'),
					value: 'brandName',
					class: 'table-header',
					align: 'start pl-2 pr-0',
				},
				{
					text: this.$t('dosage'),
					value: 'dosageInstructions',
					class: 'table-header',
					align: 'start pl-2 pr-0',
				},
				{
					text: this.$t('route'),
					value: 'route',
					class: 'table-header pl-2 pr-0',
					sortable: false,
				},
				{
					text: '',
					align: 'start pl-2 pr-0',
					sortable: false,
				},
			];
		},
		addButtonEnabled () {
			return (
				this.selectedPrescription &&
				this.dosage.trim() &&
				this.frequency.trim() &&
				this.period.trim() &&
				this.prescribingDoctor
			);
		},
	},

	watch: {
		selectedPrescription (newPrescription) {
			if (newPrescription && newPrescription.dosageInstructions) {
				this.dosage = newPrescription.dosageInstructions;
			} else {
				this.dosage = '';
			}
		},
	},

	async mounted () {
		if (!this.prescriptions) await this.getPrescriptionsForPatient();
		this.doctors = this.filterDoctors();
	},

	methods: {
		getRowsForDataTable,
		closeDrawer () {
			this.searchText = '';
			this.dosage = '';
			this.frequency = '';
			this.period = '';
			this.additionalInfo = '';
			this.prescribingDoctor = null;
			this.selectedPrescription = null;
			this.$store.dispatch('prescriptions/clearSearchedPrescriptions');
			this.$store.commit('SET_UPDATE_DETAILS_DRAWER', false);
			this.$store.commit('SET_EDIT_DETAILS_DRAWER', false);
		},

		async getPrescriptionsForPatient () {
			try {
				await this.$store.dispatch('prescriptions/getPrescriptionsByPatientId', this.detailedUser.userData.id);
			} catch (error) {
				this.$store.commit('alerts/add', {
					id: 'addedUser',
					type: 'error',
					color: 'main_red',
					message: this.$t('failed!'),
				});
			}
		},

		async searchForPrescriptions () {
			this.loading = true;
			try {
				const searchBody = {
					drugName: this.searchText,
				};
				await this.$store.dispatch('prescriptions/fdaSearch', searchBody);
			} catch (error) {
				// Change this to something more suitable
				this.$store.commit('alerts/add', {
					id: 'addedUser',
					type: 'error',
					color: 'main_red',
					message: this.$t('failed!'),
				});
			}
			this.selectedPrescription = null;
			this.loading = false;
		},

		filterNumbersOnly (inputEvent) {
			if (/\D/.test(inputEvent.key)) {
				inputEvent.preventDefault();
			}
			return true;
		},

		async addPrescription () {
			const body = {
				patientUserId: this.detailedUser.userData.id,
				staffId: this.prescribingDoctor.id,
				hospitalId: localStorage.getItem('selectedHospitalAssignedId'),
				dosage: this.dosage,
				frequency: this.frequency,
				additionalinfo: this.additionalInfo,
				brandName: this.selectedPrescription.brandName,
				genericName: this.selectedPrescription.genericName,
				productNdc: this.selectedPrescription.productNdc,
				manufacturerName: this.selectedPrescription.manufacturerName,
				route: this.selectedPrescription.route,
				warnings: this.selectedPrescription.warnings,
				adverseReactions: this.selectedPrescription.adverseReactions,
				durationDays: Number(this.period),
				prescribedAt: new Date().toISOString(),
			};
			try {
				await this.$store.dispatch('prescriptions/addPrescription', body);
				await this.getPrescriptionsForPatient();
			} catch (error) {
				// Change this to something more suitable
				this.$store.commit('alerts/add', {
					id: 'addedUser',
					type: 'error',
					color: 'main_red',
					message: this.$t('failed!'),
				});
			} finally {
				this.closeDrawer();
			}
		},

		filterDoctors () {
			return this.detailedUser.userData.medicalTeamsData[0].membersList
				.filter((member) => member.roleId === 3 && member.isSequoiaUser && member.status === 1)
				.map((member) => ({
					...member,
					fullName: `${member.firstName} ${member.lastName}`,
				}));
		},
	},
};
</script>
